/**
 * Dark Palette Definition
 *
 * Defined Sections:
 */
const DARK_PAGE_BG = "#0b2118";

export const getPaletteDark = (lightPalette) => {
  return {
  ...lightPalette,
  button: {
    ...lightPalette.button,
    secondaryBackground: "transparent",
    secondaryColor: "#00e170",
    secondaryBorder: "#00e170",
    secondaryHoverBackground: "rgba(0,225,112,0.12)",
    secondaryHoverColor: "#5FFFB4",
  },
  primary: {
    ...lightPalette.primary,
    interactive: "#00e170",
    interactiveHover: "#5FFFB4",
    softSurface: "rgba(0,225,112,0.12)",
  },
  neutral: {
    ...lightPalette.neutral,
    pageBg: DARK_PAGE_BG,
    surface: "#152a21",
    headings: "#F2F4F3",
    headingsStrong: "#F2F4F3",
    body: "#C2CBC5",
    stroke: "#2C352F",
    searchStroke: "#2C352F",
    navInactive: "#C2CBC5",
    disabled: "#8A938D",
    warmHeadings: "#F3EAE3",
    mutedText: "#A99E92",
    warmSurface: "#241B15",
    chipBg: "#1E2823",
    tickerBg: "#152a21",
    tickerBorder: "#2C352F",
    disabledSurface: "#0E1D16",
    placeholder: "#8A938D",
    locationPin: "#D4A188",
    legacySeedFormBg: "#0E1D16",
    springGoldSurface: "rgba(208, 140, 42, 0.12)",
    springGold200: "rgba(208, 140, 42, 0.08)",
    decoratedText: "#E8A287",
    warmBodyText: "#DBC0AF",
    warmStroke: "rgba(255,255,255,0.06)",
  },
  background: {
    ...lightPalette.background,
    default: "#0b2118",
    paper: "#152a21",
    tint: DARK_PAGE_BG,
    white: "#FFFFFF",
    tertiaryWhite: "#0b2118",
    butterscotch: "rgba(208, 140, 42, 0.12)",
    contactCardGradient: "linear-gradient(270deg, #18211C 0%, #12241B 100%)",
    appDownloadGradient:
      "linear-gradient(145.47deg, #12241B 0%, #18211C 50%, #0D1A10 100%)",
    // Brand surface stays light: the emblem ink is black and would vanish on a dark gradient.
    splashGradient: lightPalette.background.splashGradient,
  },
  text: {
    ...lightPalette.text,
    primary: "#F2F4F3",
    textGrey: "#C2CBC5",
    darkGreyGreen: "#C2CBC5",
    newsDate: "#A7B0AA",
    disabled: "#8A938D",
    requiredMark: "#FFB4AB",
    secondary: "#00e170",
    textGreen: "#00e170",
    error: "#FFB4AB",
    warning: "#E8B400",
    success: "#6FD98F",
  },
  formText: {
    label: "#C2CBC5",
    placeholder: "#8A938D",
    input: "#F2F4F3",
    checkbox: "#F2F4F3",
  },
  dialog: {
    title: "#F2F4F3",
    description: "#C2CBC5",
  },
  divider: "#2C352F",
  pills: {
    ...lightPalette.pills,
    info: { bg: "#10283A", text: "#7FC6F0" },
    applied: { bg: "#16301A", text: "#8FD98A" },
    success: { bg: "#16301A", text: "#8FD98A" },
    neutral: { bg: "#2A2F2C", text: "#C2CBC5", border: "#727272" },
    closed: { bg: "#2A2F2C", text: "#C2CBC5" },
    error: { bg: "#3D1A1A", text: "#FFB4AB" },
    danger: { bg: "#3D1A1A", text: "#FFB4AB" },
    prefilled: { bg: "rgba(0,225,112,0.12)", text: "#00e170" },
  },
  accent: {
    ...lightPalette.accent,
    butterscotchBg: "rgba(208, 140, 42, 0.12)",
    // On the translucent dark badge the light-mode brown is 2.5:1; this lighter amber reads 6.2:1.
    butterscotchDark: "#E8A94A",
    butterscotchDivider: "rgba(255, 255, 255, 0.24)",
    butterscotchStroke: "rgba(240, 190, 73, 0.4)",
    // primary.dark on the dark live band is 1.41:1; the dark brand green reads 8.13:1.
    liveBandText: "#00e170",
  },
  categoryTabBg: {
    green: "rgba(127, 194, 65, 0.12)",
    yellow: "rgba(208, 140, 42, 0.12)",
    grey: "rgba(255, 255, 255, 0.08)",
    blue: "rgba(85, 173, 199, 0.12)",
    peach: "rgba(184, 92, 58, 0.12)",
    purple: "rgba(155, 122, 222, 0.12)",
  },
  status: {
    ...lightPalette.status,
    open: { bg: "rgba(127, 194, 65, 0.16)", text: "#8FD98A" },
    new: { bg: "rgba(208, 140, 42, 0.16)", text: "#F3C88B" },
    closed: { bg: "#3D1A1A", text: "#FFB4AB" },
  },
  feedbackBanner: {
    ...lightPalette.feedbackBanner,
    warningBg: "rgba(208, 140, 42, 0.12)",
    errorBg: "rgba(192, 57, 43, 0.12)",
  },
  accessibility: {
    ...lightPalette.accessibility,
    bodyBg: "#0b2118",
    tileBg: "#152a21",
    tileText: "#F2F4F3",
    shortcutText: "#E8B400",
    resetBg: "rgba(0,225,112,0.12)",
    resetBorder: "#00e170",
    resetText: "#00e170",
    footerDivider: "#2C352F",
    metaText: "#8A938D",
    metaBrand: "#00e170",
    langActiveText: "#0B2118",
  },
  formMessage: {
    success: "#6FD98F",
    warning: "#E8B400",
    error: "#FFB4AB",
  },
  tab: {
    ...lightPalette.tab,
    inactiveBg: "rgba(255,255,255,0.06)",
    inactiveBorder: "#2C352F",
    inactiveText: "#F2F4F3",
    inactiveBrandText: "#00e170",
  },
  optionCard: {
    unselectedBg: "#152a21",
    unselectedBorder: "#2C352F",
    selectedBg: "rgba(0,225,112,0.12)",
    selectedShadow: "0px 2px 12px rgba(0,0,0,0.45)",
    glyph: "#00e170",
  },
  table: {
    headerBg: "#1E2823",
    rowBg: "#152a21",
    rowAltBg: "#0E1D16",
    border: "#2C352F",
  },
  statusTimeline: {
    ...lightPalette.statusTimeline,
    current: "#D2B463",
  },
  icon: {
    ...lightPalette.icon,
    faqIcon: "#16241C",
  },

  onGreen: lightPalette.onGreen,
  layout: {
    pagePadding: { xs: 2, sm: 4, md: 9 },
    pagePaddingTop: { xs: 2, md: 3 },
    pagePaddingBottom: { xs: 5, md: 7.5, lg: 10 },
    cardPadding: { xs: 2, sm: 4 },
    sectionGap: 3,
    onboardingCardMaxWidth: "460px",
    cardShadow: "0px 2px 6px rgba(0, 0, 0, 0.04)",
    softCardShadow: "0px 2px 12px rgba(0, 0, 0, 0.04)",
    dropdownShadow: "-9px -3px 15px rgba(0,46,23,0.16), 13px 16px 15.7px rgba(0,46,23,0.16)",
    selectedShadow: "0px 2px 12px rgba(0, 225, 112, 0.24)",
  },

  aqi: {
    gauge: { good: "#2E9E5B", fair: "#72AF3B", poor: "#F0BE49", severe: "#E0524F", track: "rgba(255, 255, 255, 0.2)" },
    surface: "rgba(34, 37, 41, 1)",
    text: "rgba(255, 255, 255, 0.7)",
    heading: "#ffffff",
    divider: "rgba(255, 255, 255, 0.12)",
  },
};
};
